/**
 * TransferControls - Power range inputs and directional transfer buttons
 * Used between two PowerGrid panels for moving powers
 */

import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { OathButton } from '../../../shared/OathUI';

const TransferControls = ({
  onTransferToSub,
  onTransferToMain,
  startPowerNo,
  endPowerNo,
  onStartChange,
  onEndChange,
  onSelectRange,
  canTransferToSub,
  canTransferToMain,
  agency1Name,
  agency2Name,
  pendingQueue = { movedToAgency2: [], movedToAgency1: [] },
  onUndoMove,
  onUndoAll,
  selectedCount1 = 0,
  selectedCount2 = 0,
}) => {
  const totalPending = pendingQueue.movedToAgency2.length + pendingQueue.movedToAgency1.length;

  const handleUndoAllForDirection = (powers) => {
    if (onUndoAll) {
      onUndoAll(powers);
    } else if (onUndoMove) {
      powers.forEach((powerNo) => onUndoMove(powerNo));
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 py-2 lg:py-4 w-full lg:min-w-[220px]">
      {/* Range selection */}
      <div className="w-full bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
          Select Range
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            value={startPowerNo}
            onChange={(e) => onStartChange(e.target.value)}
            placeholder="Start #"
            aria-label="Start power number"
            className="flex-1 min-w-0 px-3 py-2 lg:py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary"
          />
          <span className="text-gray-400 text-xs flex-shrink-0" aria-hidden="true">to</span>
          <input
            type="text"
            inputMode="numeric"
            value={endPowerNo}
            onChange={(e) => onEndChange(e.target.value)}
            placeholder="End #"
            aria-label="End power number"
            className="flex-1 min-w-0 px-3 py-2 lg:py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>
        <button
          onClick={onSelectRange}
          disabled={!startPowerNo && !endPowerNo}
          className="mt-2 w-full px-3 py-2 lg:py-1.5 text-xs font-medium text-primary dark:text-primary-light bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Select Powers in Range
        </button>
      </div>

      {/* Pending changes queue */}
      {totalPending > 0 && (
        <div className="w-full bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3" data-testid="pending-queue">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Pending Changes
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
              {totalPending}
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {pendingQueue.movedToAgency2.length > 0 && (
              <div className="border-l-2 border-primary pl-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary dark:bg-primary-900/40 dark:text-primary-300">
                      <ArrowRightIcon className="h-3 w-3 flex-shrink-0" />
                      {agency2Name || 'Agency 2'}
                    </span>
                  </div>
                  {onUndoMove && pendingQueue.movedToAgency2.length >= 2 && (
                    <button
                      onClick={() => handleUndoAllForDirection(pendingQueue.movedToAgency2)}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label={`Undo all moves to ${agency2Name || 'Agency 2'}`}
                    >
                      <ArrowUturnLeftIcon className="h-3 w-3" />
                      Undo all
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {pendingQueue.movedToAgency2.map((powerNo) => (
                    <div
                      key={powerNo}
                      className="flex items-center justify-between pl-2 pr-1 py-1.5 sm:py-0.5 rounded bg-white dark:bg-gray-800/50"
                    >
                      <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{powerNo}</span>
                      {onUndoMove && (
                        <button
                          onClick={() => onUndoMove(powerNo)}
                          className="p-1.5 sm:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          aria-label={`Undo move for power ${powerNo}`}
                        >
                          <XMarkIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pendingQueue.movedToAgency1.length > 0 && (
              <div className="border-l-2 border-warning pl-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300">
                      <ArrowLeftIcon className="h-3 w-3 flex-shrink-0" />
                      {agency1Name || 'Agency 1'}
                    </span>
                  </div>
                  {onUndoMove && pendingQueue.movedToAgency1.length >= 2 && (
                    <button
                      onClick={() => handleUndoAllForDirection(pendingQueue.movedToAgency1)}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label={`Undo all moves to ${agency1Name || 'Agency 1'}`}
                    >
                      <ArrowUturnLeftIcon className="h-3 w-3" />
                      Undo all
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {pendingQueue.movedToAgency1.map((powerNo) => (
                    <div
                      key={powerNo}
                      className="flex items-center justify-between pl-2 pr-1 py-1.5 sm:py-0.5 rounded bg-white dark:bg-gray-800/50"
                    >
                      <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{powerNo}</span>
                      {onUndoMove && (
                        <button
                          onClick={() => onUndoMove(powerNo)}
                          className="p-1.5 sm:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          aria-label={`Undo move for power ${powerNo}`}
                        >
                          <XMarkIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer buttons — side-by-side on mobile, stacked on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 w-full">
        <OathButton
          onClick={onTransferToSub}
          disabled={!canTransferToSub}
          variant="primary"
          size="sm"
          className="w-full group"
        >
          <span className="flex items-center justify-center gap-1.5 text-xs sm:text-sm min-w-0">
            <span className="truncate">
              {selectedCount1 > 0
                ? `Move ${selectedCount1} \u2192 ${agency2Name || 'Agency 2'}`
                : `Move to ${agency2Name || 'Agency 2'}`
              }
            </span>
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 lg:group-hover:translate-x-0.5" />
          </span>
        </OathButton>
        <OathButton
          onClick={onTransferToMain}
          disabled={!canTransferToMain}
          variant="outline"
          size="sm"
          className="w-full group"
        >
          <span className="flex items-center justify-center gap-1.5 text-xs sm:text-sm min-w-0">
            <ArrowLeftIcon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 lg:group-hover:-translate-x-0.5" />
            <span className="truncate">
              {selectedCount2 > 0
                ? `Move ${selectedCount2} \u2192 ${agency1Name || 'Agency 1'}`
                : `Move to ${agency1Name || 'Agency 1'}`
              }
            </span>
          </span>
        </OathButton>
      </div>
    </div>
  );
};

export default TransferControls;
